/**
 * La capa de carteles de ayuda: el nombre —y el atajo— de lo que tenés debajo
 * del puntero.
 *
 * Reemplaza al `title` del navegador, cuyo retardo es fijo y no se puede tocar.
 * Acá el retardo es nuestro, y sobre todo: deja de existir una vez que estás
 * leyendo. Un cartel tiene dos momentos distintos y la espera sólo sirve en el
 * primero. El primero es "¿qué es esto?": alguien se detiene sobre un botón, y
 * ahí la espera evita que el cartel salte con sólo cruzar la barra camino a
 * otra cosa. El segundo es "¿y este otro?": ya hay un cartel abierto y se
 * recorre la fila comparando. Ahí la espera es puro estorbo — la pregunta ya
 * está hecha y repetirla en cada botón convierte una lectura corrida en cinco
 * esperas. Así que después del primero, los siguientes salen en el acto y sin
 * animación: lo que cambia es la información, no el cartel.
 *
 * El `title` se muda a `data-tip` en el primer hover para que el nativo no
 * aparezca nunca; no hace falta devolverlo, de ahí en más se lee `data-tip`.
 */

/** Cuánto hay que quedarse quieto encima de algo para que aparezca el primero. */
const HOVER_DELAY_MS = 350;

/**
 * La espera en la barra lateral, que es larga a propósito.
 *
 * Ahí el puntero no pasa de visita: se recorre la lista de archivos, se apunta
 * a uno, se vuelve. Con la espera corta el cartel aparecía todo el tiempo
 * encima de lo que se estaba leyendo. Dos segundos es más que cualquier
 * recorrido normal: sólo lo ve quien se quedó mirando un ítem sin entender qué
 * era —que es justo el único momento en que el cartel ayuda.
 */
const SIDEBAR_DELAY_MS = 2000;

/** Dónde vive la espera larga. */
const SLOW_ZONE = "#sidebar";

/** La espera que le toca a este control según dónde esté. */
function delayFor(el: HTMLElement): number {
  return el.closest(SLOW_ZONE) ? SIDEBAR_DELAY_MS : HOVER_DELAY_MS;
}

/**
 * Cuánto sigue "abierta la conversación" después de que el cartel se va. Es la
 * ventana en la que pasar de un botón a otro no vuelve a esperar.
 *
 * Cubre el salto entre dos controles vecinos (que es de unos pocos frames) y el
 * segundo de duda a mitad de camino, pero no una vuelta a la pantalla más
 * tarde: eso ya es una pregunta nueva y merece su espera.
 */
const WARM_GRACE_MS = 500;

let tipEl: HTMLDivElement | null = null;
let showTimer = 0;
let currentTarget: HTMLElement | null = null;
/** ¿Venimos de un cartel? Mientras dure, los siguientes no esperan. */
let warm = false;
let coolTimer = 0;

function ensureTipEl(): HTMLDivElement {
  if (!tipEl) {
    tipEl = document.createElement("div");
    tipEl.className = "app-tip";
    tipEl.setAttribute("role", "tooltip");
    document.body.appendChild(tipEl);
  }
  return tipEl;
}

function tipTextFor(el: HTMLElement): string | null {
  if (el.hasAttribute("title")) {
    const t = el.getAttribute("title") ?? "";
    // Park the native title so the browser tooltip never shows.
    el.removeAttribute("title");
    if (t) el.setAttribute("data-tip", t);
  }
  const text = el.getAttribute("data-tip");
  return text && text.trim() ? text : null;
}

function hide() {
  window.clearTimeout(showTimer);
  showTimer = 0;
  currentTarget = null;
  // `is-instant` se va con el cartel: el próximo que salga en frío tiene que
  // volver a animarse.
  if (tipEl) tipEl.classList.remove("is-visible", "is-instant");
  // Irse de un botón no cierra la conversación: recién la cierra el reloj, si
  // no aparece otro cartel antes.
  if (warm && !coolTimer) {
    coolTimer = window.setTimeout(() => {
      warm = false;
      coolTimer = 0;
    }, WARM_GRACE_MS);
  }
}

/**
 * Muestra el cartel. `instant` lo pone en pantalla ya hecho —sin fundido ni
 * escala— porque cuando se viene de otro cartel lo que se mueve es el texto, no
 * el cartel: reanimarlo en cada botón de la fila se lee como parpadeo.
 */
function show(target: HTMLElement, text: string, instant = false) {
  const tip = ensureTipEl();
  tip.textContent = text;

  // A partir de acá hay conversación abierta: los siguientes no esperan.
  warm = true;
  window.clearTimeout(coolTimer);
  coolTimer = 0;

  // Measure while hidden so the entry animation runs from the final position.
  tip.classList.remove("is-visible");
  // Y desde el borde: un cartel que puede partir el renglón mide distinto según
  // dónde esté parado —pegado a la derecha le queda menos lugar y se parte de
  // más—, así que se mide en el rincón, con todo el ancho disponible, y recién
  // después se lo lleva a su lugar.
  tip.style.left = "0px";
  tip.style.top = "0px";
  const r = target.getBoundingClientRect();
  const tr = tip.getBoundingClientRect();
  const gap = 8;
  let top = r.bottom + gap;
  const flipped = top + tr.height > window.innerHeight - 4;
  if (flipped) top = Math.max(4, r.top - gap - tr.height);
  let left = r.left + r.width / 2 - tr.width / 2;
  left = Math.max(4, Math.min(left, window.innerWidth - tr.width - 4));

  tip.style.top = `${Math.round(top)}px`;
  tip.style.left = `${Math.round(left)}px`;
  tip.classList.toggle("is-flipped", flipped);
  tip.classList.toggle("is-instant", instant);

  if (instant) {
    // Sin transición que arrancar, el frame de espera sólo sería un frame en
    // blanco: se pinta ya.
    tip.classList.add("is-visible");
    return;
  }
  // Next frame so the class change transitions instead of snapping.
  requestAnimationFrame(() => tip.classList.add("is-visible"));
}

export function installTooltips(): void {
  document.addEventListener("pointerover", (e) => {
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      "[title], [data-tip]",
    );
    if (!target || target === currentTarget) return;
    hide();
    const text = tipTextFor(target);
    if (!text) return;
    currentTarget = target;
    const delay = delayFor(target);
    // El encadenado no vale para la espera larga: si el cartel de la barra
    // saliera en el acto por venir de otro, los dos segundos no existirían en
    // el único recorrido donde molestaban —el de la propia barra.
    const chained = warm && delay === HOVER_DELAY_MS;
    if (chained || delay <= 0) show(target, text, chained);
    else showTimer = window.setTimeout(() => show(target, text), delay);
  });

  document.addEventListener("pointerout", (e) => {
    const related = e.relatedTarget as Node | null;
    if (currentTarget && related && currentTarget.contains(related)) return;
    hide();
  });

  // Any click / scroll / key dismisses immediately.
  document.addEventListener("pointerdown", hide, true);
  document.addEventListener("keydown", hide, true);
  window.addEventListener("scroll", hide, true);
  window.addEventListener("blur", hide);
}
